import { doc, getDoc, setDoc, updateDoc, collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Remove campos desnecessários do post antes de salvar no agregado
 */
export const sanitizePostForAggregation = (postData) => {
  const { conteudo, etiquetas, ...sanitizedData } = postData;
  return sanitizedData;
};

/**
 * Atualiza o agregado de posts do blog quando um post é criado
 */
export const updateBlogPostsAggregationOnCreate = async (postData) => {
  try {
    if (!postData || !postData.id) {
      return;
    }

    const blogPostsAggregateRef = doc(db, 'agregadoPostsBlog', 'all');
    const blogPostsAggregateDoc = await getDoc(blogPostsAggregateRef);
    
    if (!blogPostsAggregateDoc.exists()) {
      // Se o agregado não existe, buscar todos os posts e criar o agregado
      try {
        const allPostsSnapshot = await getDocs(collection(db, 'postsBlog'));
        const allPosts = allPostsSnapshot.docs.map(doc => {
          const data = doc.data();
          const sanitizedData = sanitizePostForAggregation(data);
          return {
            id: doc.id,
            ...sanitizedData,
            criadoEm: data.criadoEm instanceof Date ? Timestamp.fromDate(data.criadoEm) : (data.criadoEm?.toDate ? data.criadoEm : (data.criadoEm || Timestamp.now())),
            atualizadoEm: data.atualizadoEm instanceof Date ? Timestamp.fromDate(data.atualizadoEm) : (data.atualizadoEm?.toDate ? data.atualizadoEm : (data.atualizadoEm || Timestamp.now()))
          };
        }).filter(p => p.criadoEm); // Filtrar posts sem data
        
        // Verificar se o post atual já está na lista
        const existingPostIndex = allPosts.findIndex(p => p.id === postData.id);
        const sanitizedPostData = sanitizePostForAggregation(postData);
        const postEntry = {
          id: postData.id,
          ...sanitizedPostData,
          criadoEm: postData.criadoEm instanceof Date ? Timestamp.fromDate(postData.criadoEm) : (postData.criadoEm?.toDate ? postData.criadoEm : Timestamp.fromDate(new Date(postData.criadoEm))),
          atualizadoEm: postData.atualizadoEm instanceof Date ? Timestamp.fromDate(postData.atualizadoEm) : (postData.atualizadoEm?.toDate ? postData.atualizadoEm : (postData.atualizadoEm || Timestamp.now()))
        };
        
        if (existingPostIndex === -1) {
          allPosts.push(postEntry);
        } else {
          allPosts[existingPostIndex] = postEntry;
        }
        
        // Ordenar por data de criação (mais recente primeiro)
        allPosts.sort((a, b) => {
          const dateA = a.criadoEm?.toDate ? a.criadoEm.toDate() : new Date(a.criadoEm);
          const dateB = b.criadoEm?.toDate ? b.criadoEm.toDate() : new Date(b.criadoEm);
          return dateB.getTime() - dateA.getTime();
        });
        
        await setDoc(blogPostsAggregateRef, {
          posts: allPosts,
          quantidadeTotal: allPosts.length,
          ultimaAtualizacao: Timestamp.now()
        });
        // Agregado criado
        return;
      } catch (createError) {
        // Erro ao criar agregado
        throw createError;
      }
    }

    const currentData = blogPostsAggregateDoc.data();
    const posts = currentData.posts || [];
    
    // Verificar se o post já existe
    const existingIndex = posts.findIndex(p => p.id === postData.id);
    
    if (existingIndex === -1) {
      // Adicionar novo post
      const sanitizedPostData = sanitizePostForAggregation(postData);
      const postEntry = {
        id: postData.id,
        ...sanitizedPostData,
        criadoEm: postData.criadoEm instanceof Date ? Timestamp.fromDate(postData.criadoEm) : postData.criadoEm,
        atualizadoEm: postData.atualizadoEm instanceof Date ? Timestamp.fromDate(postData.atualizadoEm) : (postData.atualizadoEm || Timestamp.now())
      };
      
      posts.push(postEntry);
      
      // Ordenar por data de criação (mais recente primeiro)
      posts.sort((a, b) => {
        const dateA = a.criadoEm?.toDate ? a.criadoEm.toDate() : new Date(a.criadoEm);
        const dateB = b.criadoEm?.toDate ? b.criadoEm.toDate() : new Date(b.criadoEm);
        return dateB.getTime() - dateA.getTime();
      });
      
      await updateDoc(blogPostsAggregateRef, {
        posts: posts,
        quantidadeTotal: posts.length,
        ultimaAtualizacao: Timestamp.now()
      });
    } else {
      // Se já existe, atualizar (caso de sincronização)
      const sanitizedPostData = sanitizePostForAggregation(postData);
      posts[existingIndex] = {
        id: postData.id,
        ...sanitizedPostData,
        criadoEm: postData.criadoEm instanceof Date ? Timestamp.fromDate(postData.criadoEm) : postData.criadoEm,
        atualizadoEm: postData.atualizadoEm instanceof Date ? Timestamp.fromDate(postData.atualizadoEm) : (postData.atualizadoEm || Timestamp.now())
      };
      
      // Reordenar
      posts.sort((a, b) => {
        const dateA = a.criadoEm?.toDate ? a.criadoEm.toDate() : new Date(a.criadoEm);
        const dateB = b.criadoEm?.toDate ? b.criadoEm.toDate() : new Date(b.criadoEm);
        return dateB.getTime() - dateA.getTime();
      });
      
      await updateDoc(blogPostsAggregateRef, {
        posts: posts,
        quantidadeTotal: posts.length,
        ultimaAtualizacao: Timestamp.now()
      });
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Atualiza o agregado de posts do blog quando um post é editado
 */
export const updateBlogPostsAggregationOnUpdate = async (postData) => {
  try {
    if (!postData || !postData.id) {
      return;
    }

    const blogPostsAggregateRef = doc(db, 'agregadoPostsBlog', 'all');
    const blogPostsAggregateDoc = await getDoc(blogPostsAggregateRef);
    
    if (!blogPostsAggregateDoc.exists()) {
      // Se o agregado não existe, criar buscando todos os posts
      try {
        const allPostsSnapshot = await getDocs(collection(db, 'postsBlog'));
        const allPosts = allPostsSnapshot.docs.map(doc => {
          const data = doc.data();
          const sanitizedData = sanitizePostForAggregation(data);
          return {
            id: doc.id,
            ...sanitizedData,
            criadoEm: data.criadoEm instanceof Date ? Timestamp.fromDate(data.criadoEm) : (data.criadoEm?.toDate ? data.criadoEm : (data.criadoEm || Timestamp.now())),
            atualizadoEm: data.atualizadoEm instanceof Date ? Timestamp.fromDate(data.atualizadoEm) : (data.atualizadoEm?.toDate ? data.atualizadoEm : (data.atualizadoEm || Timestamp.now()))
          };
        }).filter(p => p.criadoEm);
        
        const existingPostIndex = allPosts.findIndex(p => p.id === postData.id);
        const sanitizedPostData = sanitizePostForAggregation(postData);
        const postEntry = {
          id: postData.id,
          ...sanitizedPostData,
          criadoEm: postData.criadoEm instanceof Date ? Timestamp.fromDate(postData.criadoEm) : (postData.criadoEm?.toDate ? postData.criadoEm : (postData.criadoEm ? Timestamp.fromDate(new Date(postData.criadoEm)) : Timestamp.now())),
          atualizadoEm: postData.atualizadoEm instanceof Date ? Timestamp.fromDate(postData.atualizadoEm) : (postData.atualizadoEm?.toDate ? postData.atualizadoEm : (postData.atualizadoEm || Timestamp.now()))
        };
        
        if (existingPostIndex === -1) {
          allPosts.push(postEntry);
        } else {
          allPosts[existingPostIndex] = postEntry;
        }
        
        allPosts.sort((a, b) => {
          const dateA = a.criadoEm?.toDate ? a.criadoEm.toDate() : new Date(a.criadoEm);
          const dateB = b.criadoEm?.toDate ? b.criadoEm.toDate() : new Date(b.criadoEm);
          return dateB.getTime() - dateA.getTime();
        });
        
        await setDoc(blogPostsAggregateRef, {
          posts: allPosts,
          quantidadeTotal: allPosts.length,
          ultimaAtualizacao: Timestamp.now()
        });
        // Agregado criado
        return;
      } catch (createError) {
        // Erro ao criar agregado
        throw createError;
      }
    }

    const currentData = blogPostsAggregateDoc.data();
    const posts = currentData.posts || [];
    
    const existingIndex = posts.findIndex(p => p.id === postData.id);
    
    if (existingIndex !== -1) {
      // Atualizar post existente
      const sanitizedPostData = sanitizePostForAggregation(postData);
      posts[existingIndex] = {
        id: postData.id,
        ...sanitizedPostData,
        criadoEm: postData.criadoEm instanceof Date ? Timestamp.fromDate(postData.criadoEm) : postData.criadoEm,
        atualizadoEm: postData.atualizadoEm instanceof Date ? Timestamp.fromDate(postData.atualizadoEm) : (postData.atualizadoEm || Timestamp.now())
      };
      
      // Reordenar
      posts.sort((a, b) => {
        const dateA = a.criadoEm?.toDate ? a.criadoEm.toDate() : new Date(a.criadoEm);
        const dateB = b.criadoEm?.toDate ? b.criadoEm.toDate() : new Date(b.criadoEm);
        return dateB.getTime() - dateA.getTime();
      });
      
      await updateDoc(blogPostsAggregateRef, {
        posts: posts,
        quantidadeTotal: posts.length,
        ultimaAtualizacao: Timestamp.now()
      });
    } else {
      // Se não existe no agregado, adicionar (caso de sincronização)
      const sanitizedPostData = sanitizePostForAggregation(postData);
      const postEntry = {
        id: postData.id,
        ...sanitizedPostData,
        criadoEm: postData.criadoEm instanceof Date ? Timestamp.fromDate(postData.criadoEm) : postData.criadoEm,
        atualizadoEm: postData.atualizadoEm instanceof Date ? Timestamp.fromDate(postData.atualizadoEm) : (postData.atualizadoEm || Timestamp.now())
      };
      
      posts.push(postEntry);
      
      // Ordenar
      posts.sort((a, b) => {
        const dateA = a.criadoEm?.toDate ? a.criadoEm.toDate() : new Date(a.criadoEm);
        const dateB = b.criadoEm?.toDate ? b.criadoEm.toDate() : new Date(b.criadoEm);
        return dateB.getTime() - dateA.getTime();
      });
      
      await updateDoc(blogPostsAggregateRef, {
        posts: posts,
        quantidadeTotal: posts.length,
        ultimaAtualizacao: Timestamp.now()
      });
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Atualiza o agregado de posts do blog quando um post é deletado
 */
export const updateBlogPostsAggregationOnDelete = async (postId) => {
  try {
    if (!postId) {
      return;
    }

    const blogPostsAggregateRef = doc(db, 'agregadoPostsBlog', 'all');
    const blogPostsAggregateDoc = await getDoc(blogPostsAggregateRef);
    
    if (!blogPostsAggregateDoc.exists()) {
      return; // Se o agregado não existe, não há nada para fazer
    }

    const currentData = blogPostsAggregateDoc.data();
    const posts = currentData.posts || [];
    
    // Remover o post do array
    const filteredPosts = posts.filter(p => p.id !== postId);
    
    await updateDoc(blogPostsAggregateRef, {
      posts: filteredPosts,
      quantidadeTotal: filteredPosts.length,
      ultimaAtualizacao: Timestamp.now()
    });
  } catch (error) {
    throw error;
  }
};
